import { ApiService } from "../../../config";
import useUserStore from "../../../store/user";

export interface DashboardStats {
  profiles: number;
  active: number;
  pending: number;
  total: number;
  boosts?: number;
}

export interface Activity {
  id: string;
  title: string;
  time: string;
  type: "success" | "info" | "warning" | "error";
}

export interface ApiActivity {
  id?: string;
  title?: string;
  description?: string;
  time?: string;
  createdAt?: string;
  type?: "success" | "info" | "warning" | "error";
}

export interface ProfileDetails {
  id: string;
  name: string;
  category: string;
  status: string;
  stats?: {
    views: number;
    clicks: number;
    ranking: number;
  };
}

// Serviços de Home/Dashboard
export const homeService = {
  // Buscar estatísticas do dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const { user } = useUserStore.getState();

      if (!user?.token) {
        return {
          profiles: 0,
          active: 0,
          pending: 0,
          total: 0,
        };
      }

      let totalProfiles = 0;
      let activeCount = 0;
      let pendingCount = 0;

      try {
        const responseTotal = await ApiService.get(
          `/customers/tracking?limit=1`
        );

        if (responseTotal?.data?.data?.totalElements) {
          totalProfiles = responseTotal.data.data.totalElements;
        }
      } catch (error) {
        console.warn("Erro ao buscar total de perfis:", error);
      }

      try {
        const responseActive = await ApiService.get(
          `/customers/tracking?profileStatus=ATIVO&limit=1`
        );

        if (responseActive?.data?.data?.totalElements !== undefined) {
          activeCount = responseActive.data.data.totalElements;
        }
      } catch (error) {
        console.warn("Erro ao buscar perfis ATIVO:", error);
      }

      try {
        const responsePending = await ApiService.get(
          `/customers/tracking?profileStatus=ATENCAO&limit=1`
        );

        if (responsePending?.data?.data?.totalElements !== undefined) {
          pendingCount = responsePending.data.data.totalElements;
        }
      } catch (error) {
        console.warn("Erro ao buscar perfis ATENCAO:", error);
      }

      return {
        profiles: totalProfiles,
        active: activeCount,
        pending: pendingCount,
        total: totalProfiles,
        boosts: 169,
      };
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);

      return {
        profiles: 0,
        active: 0,
        pending: 0,
        total: 0,
      };
    }
  },
  
  async getActivities(): Promise<Activity[]> {
    try {
      const { user } = useUserStore.getState();

      if (!user?.token) {
        return [];
      }

      try {
        const response = await ApiService.get(
          `/activities/recent?limit=10`
        );

        if (response?.data?.data?.length > 0) {
          return response.data.data.map((activity: Record<string, unknown>) => ({
            id: activity.id || Math.random().toString(),
            title: activity.title || "Atividade",
            time: activity.time || activity.createdAt || new Date().toISOString(),
            type: (activity.type || "info") as "success" | "info" | "warning" | "error",
          }));
        }
      } catch {
        // Endpoint não disponível
      }

      return [];
    } catch (error) {
      console.error("Erro ao buscar atividades:", error);
      return [];
    }
  },

  async getProfileDashboard(profileId: string, daysToFilter: number = 30): Promise<Record<string, unknown> | null> {
    try {
      const { user } = useUserStore.getState();

      if (!user?.token || !profileId) {
        return null;
      }

      const response = await ApiService.get(
        `/dashboard/profile/${profileId}?daysToFilter=${daysToFilter}`
      );

      if (response?.data?.data) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.warn("Erro ao buscar dashboard do perfil:", error);
      return null;
    }
  },

  async getProfileDetails(profileId: string): Promise<ProfileDetails | null> {
    try {
      const { user } = useUserStore.getState();

      if (!user?.token || !profileId) {
        return null;
      }

      const response = await ApiService.get(
        `/business-profiles/${profileId}/details`
      );

      if (response?.data?.data) {
        const profileData = typeof response.data.data === 'string'
          ? JSON.parse(response.data.data)
          : response.data.data;

        return {
          id: profileId,
          name: profileData.displayName || profileData.name || "",
          category: profileData.primaryCategory || profileData.category || "Cliente",
          status: profileData.status || "active",
          stats: {
            views: profileData.analytics?.viewsInMap || 0,
            clicks: profileData.analytics?.callClicks || 0,
            ranking: 0,
          },
        };
      }

      return null;
    } catch (error) {
      console.warn("Erro ao buscar detalhes do perfil:", error);
      return null;
    }
  },
};